import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MetaTags from '../components/MetaTags';
import api from '../api/axios';
import '../components/CricketClub.css';

const DEFAULT_CLUB = {
  clubName: 'MNC West Cricket Club',
  subtitle: 'Uniting the Melbourne Nepalese community through the spirit of cricket',
  intro: 'Following preliminary player discussions held on New Year\'s Day, the MNCW Executive Team convened to formalise the structural blueprint for the organisation\'s new sports division.',
  resolution: {
    discussion: 'Following up on the preliminary player discussions held on New Year\'s Day, the team discussed the formal structural blueprint for the sports division.',
    officialName: 'MNC West Club',
    subcommitteeTask: 'bank account setup and formal team registration',
  },
  subcommittee: [
    { name: 'Keshab Pandey',    role: 'Subcommittee Member', photo: '/assets/img/team/keshab.png' },
    { name: 'Jyoti Karki',      role: 'Subcommittee Member', photo: '/assets/img/team/jyoti.jpg'  },
    { name: 'Basudev Bhusal',   role: 'Subcommittee Member', photo: '' },
    { name: 'Prajwol Bhattari', role: 'Subcommittee Member', photo: '' },
  ],
  gallery: [
    { url: '/assets/img/banner1.jpg', caption: 'Community gathering', wide: true },
    { url: '/assets/img/banner2.jpg', caption: 'Cultural celebration', wide: false },
  ],
};

function MemberCard({ member }) {
  return (
    <div className="cc-member-card">
      <div className="cc-member-photo">
        {member.photo
          ? <img src={member.photo} alt={member.name} />
          : <i className="fas fa-user"></i>
        }
      </div>
      <div className="cc-member-info">
        <h4>{member.name}</h4>
        <p>{member.role}</p>
      </div>
    </div>
  );
}

export default function CricketClubPage() {
  const [site, setSite]   = useState(null);
  const [club, setClub]   = useState(DEFAULT_CLUB);

  useEffect(() => {
    fetch('/content.json')
      .then(r => r.json())
      .then(d => setSite(d.site))
      .catch(() => {});

    api.get('/content/cricketclub')
      .then(r => setClub(prev => ({ ...prev, ...r.data })))
      .catch(() => {});
  }, []);

  if (!site) {
    return (
      <div className="page-loading">
        <div className="spinner-ring"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <MetaTags
        title="Cricket Club | Melbourne Nepalese Community West"
        description="MNC West Cricket Club — officially established under Melbourne Nepalese Community West to bring the community together through the sport of cricket."
        url="https://mncw.org.au/cricket-club"
        image="https://mncw.org.au/assets/img/logo.jpg"
        type="website"
      />
      <Header data={site} />

      {/* ── Hero ── */}
      <div
        className="cc-hero"
        style={{ background: 'linear-gradient(135deg, rgba(0,56,147,0.88) 0%, rgba(220,20,60,0.82) 100%), url(/assets/img/banner1.jpg) center/cover no-repeat' }}
      >
        <div className="cc-hero-overlay">
          <div className="container cc-hero-content">
            <div className="cc-hero-logo">
              <img src={club.logo || '/assets/img/logo.jpg'} alt="Cricket Club Logo" />
            </div>
            <div className="cc-hero-text">
              <span className="cc-hero-label">Sports Division</span>
              <h1>{club.clubName}</h1>
              <p>{club.subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Join CTA ── */}
      <section className="cc-join-section">
        <div className="container cc-join-content">
          <div className="cc-join-text">
            <span className="cc-label" style={{ color: 'var(--primary-red)' }}>Open to All</span>
            <h2>We welcome cricket players from Melbourne West to join our team</h2>
            <p>
              Whether you're an experienced player or just love the game, MNC West Cricket Club
              is building a squad that represents our community. Sign up as an MNCW member to get
              involved.
            </p>
            <div className="cc-join-buttons">
              <Link to="/membership#pricing" className="cc-join-btn">
                <i className="fas fa-user-plus"></i> Join / Become a Member
              </Link>
              <a href="/feedback" className="cc-join-btn cc-join-btn-outline">
                <i className="fas fa-envelope"></i> Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Subcommittee ── */}
      <section className="cc-section cc-section-light">
        <div className="container">
          <div className="cc-section-header">
            <span className="cc-label">Founding Members</span>
            <h2>Club Subcommittee</h2>
            <div className="cc-rule"></div>
            <p className="cc-section-intro">
              These four members were appointed by the Executive Team to lead the foundational
              setup of MNC West Cricket Club.
            </p>
          </div>

          <div className="cc-members-grid">
            {(club.subcommittee || []).map(m => <MemberCard key={m.name} member={m} />)}
          </div>
        </div>
      </section>

      {/* ── Gallery / Coming Soon ── */}
      <section className="cc-section cc-section-light">
        <div className="container">
          <div className="cc-section-header">
            <span className="cc-label">Photos</span>
            <h2>Club Gallery</h2>
            <div className="cc-rule"></div>
          </div>

          <div className="cc-gallery-grid">
            {(club.gallery || []).map((img, i) => (
              <div key={i} className={`cc-gallery-item${img.wide ? ' cc-gallery-wide' : ''}`}>
                <img src={img.url} alt={img.caption || 'MNCW Cricket Club'} />
                {img.caption && <div className="cc-gallery-caption">{img.caption}</div>}
              </div>
            ))}
            <div className="cc-gallery-item cc-gallery-placeholder">
              <i className="fas fa-camera"></i>
              <span>Match photos coming soon</span>
            </div>
          </div>
        </div>
      </section>

      <Footer data={site} />
    </div>
  );
}
